import React, { memo, useState } from 'react';
import { checkTime, createHandle, getCity, toMoney } from '@/libs/app';
import { TFunction } from 'next-i18next';
import { Product } from '@/features/products/shared';
import {
  AuctionStatus,
  DiscountStatus,
  PagePath,
  PriceSymbol,
  ProductStatus,
} from '@/layouts/shared';
import { Link, LiveProduct } from '@/components';
import { CountDown } from '../info/countdown';

export type InfoPartialProps = {
  data: Product;
  t: TFunction;
  isAuth: boolean;
  onRemindClick: (value: boolean) => void;
};

const InfoMobilePartial = ({
  data,
  isAuth,
  t,
  onRemindClick,
}: InfoPartialProps) => {
  const {
    is_auction: isAuction,
    is_partner: isPartner,
    price,
    token,
    product_code: productCode,
    product_name_detail: productNameDetail,
    cat_name: cateName,
    live_stream_product: liveStreamProducts,
    tags,
    product_discount,
    quotation,
    status,
    auction,
    branch,
  } = data ?? {};

  const {
    color: color,
    gem_size: gemSize,
    gem_type: gemType,
    gem_pure: gemPure,
    gem_small: gemSmall,
    gem_weight: gemWeight,
    gem_material: gemMaterial,
    jewelry_material: jewelryMaterial,
    ni_number: niNumber,
    gem_master_size: gemMasterSize,
  } = data?.attributes ?? {};

  const items = [
    { label: t('detail.label.gem-material'), value: gemMaterial },
    { label: t('detail.label.gem-master-size'), value: gemMasterSize },
    { label: t('detail.label.gem-type'), value: gemType },
    { label: t('detail.label.color'), value: color },
    { label: t('detail.label.gem-pure'), value: gemPure },
    { label: t('detail.label.gem-small'), value: gemSmall },
    { label: t('detail.label.jewelry-t-material'), value: jewelryMaterial },
    { label: t('detail.label.ni-number'), value: niNumber },
    { label: t('detail.label.gem-size'), value: gemSize },
    { label: t('detail.label.gem-weight'), value: gemWeight },
  ];
  const [checked, setChecked] = useState<boolean>(
    data.reminding_resell_product ? true : false
  );

  let isTimeDiscount = false;
  if (product_discount) {
    isTimeDiscount =
      new Date(new Date().toISOString()).getTime() -
        new Date(new Date(product_discount?.ended_at).toISOString()).getTime() <
      0;
  }
  const [show, setShow] = useState<boolean>(true);
  const showCountDown = () => {
    setShow(false);
  };
  const remindProduct = () => {
    setChecked(!checked);
    onRemindClick(!checked);
  };
  let isDiscount = product_discount
    ? checkTime(product_discount?.started_at, product_discount?.ended_at)
    : false;
  if (product_discount && product_discount?.status === DiscountStatus.Ended)
    isDiscount = false;
  return (
    <div className="tw-flex tw-flex-col tw-pt-5 tw-gap-y-4 tw-overflow-hidden">
      {show && isDiscount && (
        <>
          <div className="tw-px-4 tw-flex tw-flex-col tw-items-center tw-gap-y-3">
            <div className=" tw-text-secondary-70 tw-text-base">
              {t('detail.label.time-discount')}
            </div>
            <CountDown
              startTime={product_discount.started_at}
              endTime={product_discount.ended_at}
              showCountDown={showCountDown}
              t={t}
            />
          </div>
        </>
      )}
      <div className="tw-w-full tw-px-4">
        <div>
          <h2
            dangerouslySetInnerHTML={{ __html: productNameDetail }}
            className="tw-leading-6 tw-text-lg tw-line-clamp-2 tw-text-black tw-font-semibold"
          />
          <h3
            dangerouslySetInnerHTML={{ __html: productNameDetail }}
            className="tw-hidden"
          />
          <p className="tw-mt-1 tw-text-secondary-70">
            {t('detail.label.code', { value: productCode })} |{' '}
            {t('detail.label.cate', { value: cateName })}{' '}
            {isPartner && ` | ${getCity(branch)}`}
          </p>
          {tags && !!tags.length && (
            <div className="tw-mt-1 tw-text-secondary-70">
              tags:{' '}
              {tags.map((tag, index) => (
                <span key={`tag-${index}`}>
                  <Link
                    url={{
                      pathname: PagePath.ProductsPage,
                      query: { tag: tag.tag_name },
                    }}
                    className="!tw-text-blue-50"
                  >
                    #{tag.tag_name}
                  </Link>
                  {index < tags.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          )}
        </div>
        {isAuction && auction && auction.status != AuctionStatus.Ended ? (
          <>
            <div className="tw-text-[#979797] tw-flex tw-items-center tw-mb-3 tw-line-through">
              <span>Giá khởi điểm:&nbsp;</span>
              <div className="tw-flex tw-items-center tw-justify-center">
                <span>{toMoney(auction.origin_price, t)}</span>
                <span>&nbsp;~&nbsp;</span>
                <span>
                  {toMoney(auction.origin_price / 100000, t, PriceSymbol.Atd)}
                </span>
              </div>
            </div>
            <div className="tw-gap-x-2 tw-mt-1 tw-leading-8 tw-text-26 tw-border tw-border-dashed tw-border-gray-400 tw-py-4 tw-w-full  tw-rounded-full !tw-text-lg md:!tw-text-xl">
              <p className="tw-text-center">Giá hiện tại</p>
              <div className="tw-flex tw-items-center tw-justify-center">
                <span className="tw-font-bold tw-text-warning-90">
                  {toMoney(auction.sell_price || 0, t)}
                </span>
                <span className="tw-font-bold">&nbsp;~&nbsp;</span>
                <span className="tw-font-bold tw-text-primary-main">
                  {toMoney(
                    (auction.sell_price || 0) / 100000,
                    t,
                    PriceSymbol.Atd
                  )}
                </span>
              </div>
            </div>
          </>
        ) : (
          <>
            {!quotation && (
              <div className="tw-pt-4 tw-pb-2">
                {isDiscount && (
                  <div className="tw-mt-1">
                    <span className="tw-line-through tw-text-base tw-text-gray-60">
                      {t('detail.label.start-price')}: {toMoney(price, t)} ~{' '}
                      {toMoney(token, t, PriceSymbol.Atd)}
                    </span>
                  </div>
                )}
                <div className="tw-gap-x-2 tw-mt-1 tw-leading-8 tw-text-26 tw-border tw-border-dashed tw-border-gray-400 tw-py-4 tw-w-full tw-flex tw-items-center tw-justify-center tw-rounded-full !tw-text-lg md:!tw-text-xl">
                  {show && isDiscount ? (
                    <>
                      <span className="tw-font-bold tw-text-warning-90">
                        {toMoney(
                          price - (price * product_discount.discount) / 100,
                          t
                        )}
                      </span>
                      <span className="tw-font-bold">~</span>
                      <span className="tw-font-bold tw-text-primary-main">
                        {toMoney(
                          token - (token * product_discount.discount) / 100,
                          t,
                          PriceSymbol.Atd
                        )}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="tw-font-bold tw-text-warning-90">
                        {toMoney(price, t)}
                      </span>
                      <span className="tw-font-bold">&nbsp;~&nbsp;</span>
                      <span className="tw-font-bold tw-text-primary-main">
                        {toMoney(token, t, PriceSymbol.Atd)}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {!isPartner && (
          <div
            className={`tw-flex tw-justify-end tw-py-5 ${
              isAuth && status != ProductStatus.Availability
                ? ''
                : 'tw-border-b tw-border-primary-main tw-border-opacity-50'
            }`}
          >
            <Link
              url="/guide-to-measuring-hands/"
              className="!tw-text-secondary-70 tw-text-sm !tw-underline tw-leading-7 tw-italic"
            >
              Hướng dẫn đo kích thước và quy đổi size
            </Link>
          </div>
        )}
        {isAuth && status != ProductStatus.Availability && (
          <div className="tw-flex tw-border-b tw-items-center tw-border-primary-main tw-border-opacity-50 tw-pb-6 tw-gap-x-5">
            <span className="tw-text-sm tw-flex-1 tw-italic">
              Nhận thông báo khi sản phẩm được mở bán lại
            </span>
            <input
              className="tw-mr-2 tw-mt-[0.3rem] tw-h-6 tw-w-10 tw-appearance-none tw-rounded-[3rem] tw-bg-[#D9D9D9] before:tw-pointer-events-none before:tw-absolute before:tw-h-3.5 before:tw-w-3.5 before:tw-rounded-full before:tw-bg-transparent before:tw=-content-[''] after:tw-absolute after:tw-z-[2] after:tw-mt-[0.125rem] after:tw-ml-[0.15rem] after:tw-h-5 after:tw-w-5 after:tw-rounded-full after:tw-border-none after:tw-bg-[#D9D9D9] after:tw-shadow-[0_0px_3px_0_rgb(0_0_0_/_7%),_0_2px_2px_0_rgb(0_0_0_/_4%)] after:tw-transition-[background-color_0.2s,transform_0.2s] after:tw-content-[''] checked:tw-bg-primary-main checked:after:tw-absolute checked:after:tw-z-[2] checked:after:tw-mt-[0.125rem] checked:after:tw-ml-[1.0625rem] checked:after:tw-h-5 checked:after:tw-w-5 checked:after:tw-rounded-full checked:after:tw-border-none checked:after:tw-bg-primary checked:after:tw-shadow-[0_4px_1px_-2px_rgba(0,0,0,0.2),_0_2px_2px_0_rgba(0,0,0,0.14),_0_1px_5px_0_rgba(0,0,0,0.12)] checked:after:tw-transition-[background-color_0.2s,transform_0.2s] checked:after:tw-content-[''] hover:tw-cursor-pointer focus:tw-outline-none focus:tw-ring-0 focus:before:tw-scale-100 focus:before:tw-opacity-0 focus:before:tw-shadow-[3px_-1px_0px_13px_rgba(0,0,0,0.6)] focus:before:tw-transition-[box-shadow_0.2s,transform_0.2s] focus:after:tw-absolute focus:after:tw-z-[1] focus:after:tw-block focus:after:tw-h-5 focus:after:tw-w-5 focus:after:tw-rounded-full focus:after:tw-content-[''] checked:focus:tw-border-primary-main checked:focus:tw-bg-primary-main checked:focus:before:tw-ml-[1.0625rem] checked:focus:before:tw-scale-100 checked:focus:before:tw-shadow-[3px_-1px_0px_13px_#3b71ca] checked:focus:before:tw-transition-[box-shadow_0.2s,transform_0.2s] dark:tw-bg-[#D9D9D9] dark:after:tw-bg-white dark:checked:tw-bg-primary dark:checked:after:tw-bg-primary dark:focus:before:tw-shadow-[3px_-1px_0px_13px_rgba(255,255,255,0.4)] dark:checked:focus:before:tw-shadow-[3px_-1px_0px_13px_#3b71ca]"
              type="checkbox"
              role="switch"
              id="flexSwitchCheckDefault01"
              checked={checked}
              onChange={() => remindProduct()}
            />
          </div>
        )}
        <div className="tw-flex tw-gap-y-1 tw-flex-col tw-pb-6 tw-pt-2">
          <div className="">
            {items.map(({ value, label }, index) => {
              if (value) {
                return (
                  <div
                    className="tw-flex tw-justify-between tw-items-center tw-text-lg"
                    key={`item-index-${index}`}
                  >
                    <span
                      className="tw-font-light tw-text-base tw-text-secondary-70"
                      dangerouslySetInnerHTML={{ __html: label }}
                    />
                    <span
                      className="tw-font-normal tw-text-base tw-ml-3"
                      dangerouslySetInnerHTML={{ __html: value }}
                    />
                  </div>
                );
              }
            })}
          </div>
          {!isPartner && (
            <div className="tw-mt-3 tw-flex tw-flex-col tw-text-base tw-gap-2">
              <Link
                url={{ pathname: PagePath.ProductWarrantyPolicyPage }}
                className="!tw-text-blue-50 tw-italic"
                target="_blank"
              >
                Xem chính sách bảo hành sản phẩm
              </Link>
              <Link
                url={{ pathname: PagePath.RefundPolicyPage }}
                className="!tw-text-blue-50 tw-italic"
                target="_blank"
              >
                Xem chính sách thu đổi
              </Link>
              <Link
                url={{ pathname: PagePath.ConsignPolicyPage }}
                className="!tw-text-blue-50 tw-italic"
                target="_blank"
              >
                Xem chính sách mua bán/ký gửi sản phẩm
              </Link>
            </div>
          )}
          {!!liveStreamProducts && !isPartner && (
            <div className="tw-mt-10 ">
              <h3 className="">
                <span className="tw-text-base tw-font-light">
                  {t('detail.label.view')}
                </span>
              </h3>
              <div className="tw-mt-3 tw-grid tw-gap-4">
                {liveStreamProducts.map((live) => {
                  let handle = `${createHandle(live.live_stream.name)}-${
                    live.live_stream_id
                  }`;
                  return (
                    live.status &&
                    live.start_datetime && (
                      <Link
                        url={{
                          pathname: PagePath.LiveVideoPage,
                          query: { id: handle, start: live.played },
                        }}
                        key={'live-' + live.live_stream_id}
                      >
                        <div className="tw-bg-muted-main tw-py-3 tw-px-4 tw-rounded-5">
                          <LiveProduct data={live} />
                        </div>
                      </Link>
                    )
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(InfoMobilePartial);
